/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import {
  CheckCircle2,
  Download,
  FileText,
  Inbox,
  Loader2,
  RefreshCcw,
  Search,
  User,
} from "lucide-react";
import axiosInstance from "../../services/Axios/axios";
import RenderAdminPage from "../RenderAdminPage";
import { LOGO_URL, ORG } from "../../helpers/brand";
import {
  downloadFundStatement,
  shortDate,
  isoDate,
  describeTransaction,
  sortRowsNewestFirst,
} from "../../helpers/fundStatementPdf";

const ALL_ROWS = 2000;

const cedi = (n) =>
  Number(n || 0).toLocaleString("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function MemberStatement() {
  const [staffId, setStaffId] = useState("");
  const [searching, setSearching] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [member, setMember] = useState(null);
  const [rows, setRows] = useState(null);
  const [requests, setRequests] = useState([]);
  const [requestStatus, setRequestStatus] = useState("requested");
  const [requestSearch, setRequestSearch] = useState("");
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [processingRequestId, setProcessingRequestId] = useState(null);

  const total = useMemo(
    () => (rows || []).reduce((sum, r) => sum + Number(r.deduction || 0), 0),
    [rows]
  );
  const ordered = useMemo(() => sortRowsNewestFirst(rows || []), [rows]);

  const fetchRequests = async () => {
    setLoadingRequests(true);
    try {
      const params = new URLSearchParams({
        status: requestStatus,
        search: requestSearch.trim(),
      });
      const res = await axiosInstance.get(`/funds/statement-requests?${params.toString()}`);
      setRequests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Could not load statement requests");
    } finally {
      setLoadingRequests(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [requestStatus]);

  const search = async (e, staffIdOverride) => {
    e?.preventDefault();
    const id = String(staffIdOverride || staffId).trim();
    if (!id) return toast.info("Enter a staff ID to search");

    setStaffId(id);
    setSearching(true);
    setMember(null);
    setRows(null);
    try {
      const [memberRes, fundsRes] = await Promise.allSettled([
        axiosInstance.get(`/users/members/${encodeURIComponent(id)}`),
        axiosInstance.get(
          `/funds?isPaginated=true&page=1&size=${ALL_ROWS}&search=${encodeURIComponent(id)}`
        ),
      ]);

      const page = fundsRes.status === "fulfilled" ? fundsRes.value?.data?.data : null;
      const returned = page?.docs ?? [];
      const fundRows = returned.filter((r) => String(r.staff_id) === id);

      if (returned.length >= ALL_ROWS && Number(page?.totalItems) > returned.length) {
        toast.warn(
          `Only the first ${returned.length} of ${page.totalItems} records matching "${id}" were loaded.`
        );
      }

      const memberDoc =
        (memberRes.status === "fulfilled" && memberRes.value?.data) ||
        fundRows.find((r) => r?.user?.name)?.user ||
        null;

      if (!memberDoc && !fundRows.length) {
        toast.error(`No member or contributions found for ${id}`);
        setRows([]);
        return;
      }

      setMember({ staff_id: id, ...(memberDoc || {}) });
      setRows(fundRows);
      if (!fundRows.length) toast.info("Member found, but no contributions recorded");
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Search failed. Try again.");
      setRows([]);
    } finally {
      setSearching(false);
    }
  };

  const download = async () => {
    if (!member || !ordered.length) return toast.info("Nothing to download");
    setDownloading(true);
    try {
      await downloadFundStatement({ member, rows: ordered });
    } catch (err) {
      console.error(err);
      toast.error("Could not build the statement PDF");
    } finally {
      setDownloading(false);
    }
  };

  const completeRequest = async (requestId) => {
    setProcessingRequestId(requestId);
    try {
      await axiosInstance.put(
        `/funds/statement-requests/complete?request_id=${encodeURIComponent(requestId)}`
      );
      toast.success("Statement request marked as completed");
      fetchRequests();
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Could not update request");
    } finally {
      setProcessingRequestId(null);
    }
  };

  return (
    <RenderAdminPage title="Member Statement">
      <p className="text-sm text-slate-500 dark:text-slate-400 -mt-2 mb-6">
        Review member statement requests, then open a staff ID to download the
        watermarked fund contribution statement.
      </p>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm p-5 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
              <Inbox size={21} />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-800 dark:text-slate-100">
                Requested Statements
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Requests submitted from the mobile app.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={requestStatus}
              onChange={(e) => setRequestStatus(e.target.value)}
              className="px-3 py-2.5 rounded-xl border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-200 text-sm outline-none"
            >
              <option value="requested">Requested</option>
              <option value="completed">Completed</option>
              <option value="all">All</option>
            </select>
            <div className="relative">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={requestSearch}
                onChange={(e) => setRequestSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") fetchRequests();
                }}
                placeholder="Search requests"
                className="w-full sm:w-56 pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-slate-800 dark:text-slate-200 text-sm outline-none"
              />
            </div>
            <button
              onClick={fetchRequests}
              disabled={loadingRequests}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-gray-700 hover:bg-slate-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              {loadingRequests ? <Loader2 size={15} className="animate-spin" /> : <RefreshCcw size={15} />}
              Refresh
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 dark:bg-gray-700/50">
                {["Member", "Staff ID", "Email", "Facility", "Requested", "Status", "Action"].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
              {loadingRequests ? (
                <tr>
                  <td colSpan="7" className="px-4 py-10 text-center text-sm text-slate-400">
                    Loading statement requests...
                  </td>
                </tr>
              ) : requests.length ? (
                requests.map((request) => (
                  <tr key={request._id} className="hover:bg-slate-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-3 text-sm font-semibold text-slate-800 dark:text-slate-100">
                      {request.name || "Unknown member"}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                      {request.staff_id}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                      {request.email}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                      {request.facility || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
                      {formatDateTime(request.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${
                        request.status === "completed"
                          ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                          : "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                      }`}>
                        {request.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => search(null, request.staff_id)}
                          className="px-3 py-2 rounded-lg text-xs font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300"
                        >
                          Open Statement
                        </button>
                        {request.status !== "completed" && (
                          <button
                            onClick={() => completeRequest(request._id)}
                            disabled={processingRequestId === request._id}
                            className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 disabled:opacity-50"
                          >
                            {processingRequestId === request._id ? (
                              <Loader2 size={13} className="animate-spin" />
                            ) : (
                              <CheckCircle2 size={13} />
                            )}
                            Complete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-4 py-10 text-center text-sm text-slate-400">
                    No statement requests found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <form
        onSubmit={search}
        className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm p-5 mb-6"
      >
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1">
            <label
              htmlFor="staffId"
              className="block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              Staff ID
            </label>
            <div className="relative">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                id="staffId"
                type="search"
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
                placeholder="e.g. 901925"
                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-slate-800 dark:text-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all placeholder:text-slate-400"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={searching}
            className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-white text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {searching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            {searching ? "Searching..." : "Search"}
          </button>
        </div>
      </form>

      {member && (
        <>
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm p-5 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 flex-shrink-0">
                  {member.profile_image ? (
                    <img
                      src={member.profile_image}
                      alt={member.name}
                      className="w-12 h-12 rounded-xl object-cover"
                    />
                  ) : (
                    <User size={22} />
                  )}
                </div>
                <div>
                  <p className="text-base font-bold text-slate-800 dark:text-slate-100">
                    {member.name || "Unknown member"}
                  </p>
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                    {member.staff_id}
                  </p>
                  <dl className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-1 text-xs">
                    {[
                      ["Facility", member.facility],
                      ["Department", member.department],
                      ["District", member.district],
                      ["Region", member.region],
                      ["Phone", member.phone_number],
                      ["Statement as at", isoDate()],
                    ].map(([k, v]) => (
                      <div key={k}>
                        <dt className="text-slate-400 dark:text-slate-500">{k}</dt>
                        <dd className="text-slate-700 dark:text-slate-300 font-medium">
                          {v || "-"}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>

              <div className="flex flex-col items-start sm:items-end gap-3">
                <div className="text-right">
                  <p className="text-xs text-slate-400 dark:text-slate-500">Total contributions</p>
                  <p className="text-2xl font-black text-indigo-600 dark:text-indigo-400">
                    GHS {cedi(total)}
                  </p>
                  <p className="text-xs text-slate-400">{ordered.length} entries</p>
                </div>
                <button
                  onClick={download}
                  disabled={downloading || !ordered.length}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {downloading ? (
                    <Loader2 size={15} className="animate-spin" />
                  ) : (
                    <Download size={15} />
                  )}
                  {downloading ? "Preparing..." : "Download Statement"}
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 dark:border-gray-700">
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">
                Fund Contributions
              </h3>
            </div>
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0">
                  <tr className="bg-slate-50 dark:bg-gray-700/50">
                    {["Date", "Description", "Reference", "Period"].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Deduction
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                  {ordered.length ? (
                    ordered.map((r, i) => (
                      <tr
                        key={(r._id || r.period) + i}
                        className="hover:bg-slate-50 dark:hover:bg-gray-700/30 transition-colors"
                      >
                        <td className="px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300">
                          {shortDate(r.date)}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300">
                          {describeTransaction(r.transaction_type)}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-slate-600 dark:text-slate-400">
                          {r.reference || r.period || "-"}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-slate-600 dark:text-slate-400">
                          {r.period || "-"}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-right font-semibold text-slate-700 dark:text-slate-300">
                          GHS {cedi(r.deduction)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="5"
                        className="px-4 py-12 text-center text-sm text-slate-400 dark:text-slate-500"
                      >
                        No contributions recorded for this member
                      </td>
                    </tr>
                  )}
                </tbody>
                {ordered.length > 0 && (
                  <tfoot>
                    <tr className="bg-slate-50 dark:bg-gray-700/50 border-t-2 border-slate-200 dark:border-gray-600">
                      <td
                        colSpan="4"
                        className="px-4 py-3 text-sm font-bold text-slate-800 dark:text-slate-100"
                      >
                        TOTAL
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-black text-indigo-600 dark:text-indigo-400">
                        GHS {cedi(total)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}

      {!member && !searching && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-slate-200 dark:border-gray-700 p-12 text-center">
          <img
            src={LOGO_URL}
            alt=""
            className="h-16 w-auto mx-auto mb-4 opacity-40"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <FileText size={28} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Search a staff ID to load that member's {ORG.name.join(" ")} contributions.
          </p>
        </div>
      )}

      <ToastContainer autoClose={3000} />
    </RenderAdminPage>
  );
}

export default MemberStatement;
